import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";

import { TextField } from "@/components/form/TextField";
import { useAutosaveForm } from "@/components/form/useAutosaveForm";
import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  saveLocalPartner,
  saveSuperUser,
  useCancelInvitation,
  useInviteLocalPartner,
  useInviteSuperUser,
  useLocalPartnersQuery,
  usePendingInvitations,
  useResendInvitation,
  useSuperUsersQuery,
} from "../api";
import { apiErrorDetail } from "../lib/errors";
import {
  InvitationType,
  type LocalPartner,
  type PatchedLocalPartner,
  type PatchedSuperUser,
  type SuperUser,
} from "../types";

function fullName(u: {
  first_name?: string;
  last_name?: string;
  email: string;
}): string {
  return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email;
}

function toStr(n: number | null | undefined): string {
  return n == null ? "" : String(n);
}

/**
 * What to print in a local partner's licence column.
 *
 * `max_group_licenses` caps the members a user can sponsor across the groups
 * they *own*. A local partner invited into someone else's group owns none, so
 * their pool is never consulted — the members they invite come out of the group
 * owner's. Printing "unlimited" there announces a power they do not have, which
 * is only noise in a list an admin is scanning.
 */
function licencePool(
  lp: { max_group_licenses?: number | null; owns_group?: boolean },
  t: (key: string) => string,
): string {
  if (lp.owns_group === false) return t("partners.localPartners.groupLicences");
  return lp.max_group_licenses == null
    ? t("partners.superUsers.unlimited")
    : String(lp.max_group_licenses);
}

interface PoolValues {
  max_groups: string;
  max_group_licenses: string;
  discount_type: PatchedSuperUser["discount_type"];
  discount_value: string;
  discount_duration_years: string;
}

/** Autosaving pool-settings editor for one regional partner. */
function PoolEditor({ user, onClose }: { user: SuperUser; onClose: () => void }) {
  const { t } = useTranslation();

  const isPercentage = user.discount_type !== "fixed";
  const { form, autosave } = useAutosaveForm<PoolValues>({
    defaultValues: {
      max_groups: toStr(user.max_groups),
      max_group_licenses: toStr(user.max_group_licenses),
      discount_type: user.discount_type ?? "percentage",
      discount_value: isPercentage
        ? (user.discount_percentage ?? "")
        : (user.discount_amount ?? ""),
      discount_duration_years: toStr(user.discount_duration_years),
    },
    autosave: {
      save: (values, signal) => {
        const percentage = values.discount_type === "percentage";
        const body: PatchedSuperUser = {
          max_groups:
            values.max_groups.trim() === "" ? null : Number(values.max_groups),
          max_group_licenses:
            values.max_group_licenses.trim() === ""
              ? null
              : Number(values.max_group_licenses),
          discount_type: values.discount_type,
          discount_percentage:
            percentage && values.discount_value.trim() !== ""
              ? values.discount_value
              : null,
          discount_amount:
            !percentage && values.discount_value.trim() !== ""
              ? values.discount_value
              : null,
          discount_duration_years:
            values.discount_duration_years.trim() === ""
              ? null
              : Number(values.discount_duration_years),
        };
        return saveSuperUser(user.id, body, signal);
      },
      debounceMs: 600,
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between">
        <CardTitle className="text-base">
          {t("partners.superUsers.editHeading", { name: fullName(user) })}
        </CardTitle>
        <SaveStatus status={autosave.status} />
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 md:grid-cols-2"
          onSubmit={(e) => e.preventDefault()}
        >
          <form.Field name="max_groups">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.superUsers.maxGroupsLabel")}
              />
            )}
          </form.Field>
          <form.Field name="max_group_licenses">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.superUsers.maxLicensesLabel")}
              />
            )}
          </form.Field>

          <form.Field name="discount_type">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor={field.name}>
                  {t("partners.superUsers.discountTypeLabel")}
                </Label>
                <select
                  id={field.name}
                  name={field.name}
                  value={field.state.value ?? "percentage"}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value as PatchedSuperUser["discount_type"],
                    )
                  }
                  onBlur={field.handleBlur}
                  className="flex min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="percentage">
                    {t("partners.superUsers.discountPercentage")}
                  </option>
                  <option value="fixed">
                    {t("partners.superUsers.discountFixed")}
                  </option>
                </select>
              </div>
            )}
          </form.Field>
          <form.Field name="discount_value">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.superUsers.discountValueLabel")}
              />
            )}
          </form.Field>

          <form.Field name="discount_duration_years">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.superUsers.discountDurationLabel")}
              />
            )}
          </form.Field>
        </form>
        <div className="mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("partners.superUsers.close")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Comma/newline-separated names → a trimmed, de-blanked list. */
function parseNames(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Inline invite form for a new regional partner (max pools + discount + pre-created groups). */
function InviteSuperUserForm() {
  const { t } = useTranslation();
  const invite = useInviteSuperUser();
  const [email, setEmail] = useState("");
  const [maxGroups, setMaxGroups] = useState("");
  const [maxLicenses, setMaxLicenses] = useState("");
  const [discountType, setDiscountType] =
    useState<PatchedSuperUser["discount_type"]>("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [discountDuration, setDiscountDuration] = useState("");
  const [preGroups, setPreGroups] = useState("");

  const reset = () => {
    setEmail("");
    setMaxGroups("");
    setMaxLicenses("");
    setDiscountValue("");
    setDiscountDuration("");
    setPreGroups("");
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const percentage = discountType === "percentage";
    const value = discountValue.trim();
    invite.mutate(
      {
        email: email.trim(),
        max_groups: maxGroups.trim() === "" ? null : Number(maxGroups),
        max_group_licenses:
          maxLicenses.trim() === "" ? null : Number(maxLicenses),
        discount_type: value === "" ? undefined : discountType,
        discount_percentage: percentage && value !== "" ? value : null,
        discount_amount: !percentage && value !== "" ? value : null,
        discount_duration_years:
          discountDuration.trim() === "" ? null : Number(discountDuration),
        pre_group_names: parseNames(preGroups),
      },
      {
        onSuccess: () => {
          toast.success(t("partners.inviteSuperUser.sent", { email: email.trim() }));
          reset();
        },
        onError: (err) => toast.error(apiErrorDetail(err) ?? t("partners.actionFailed")),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {t("partners.inviteSuperUser.heading")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
            <Label htmlFor="su-email">{t("partners.inviteSuperUser.emailLabel")}</Label>
            <Input
              id="su-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-max-groups">
              {t("partners.inviteSuperUser.maxGroupsLabel")}
            </Label>
            <Input
              id="su-max-groups"
              type="number"
              min={0}
              value={maxGroups}
              onChange={(e) => setMaxGroups(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-max-licenses">
              {t("partners.inviteSuperUser.maxLicensesLabel")}
            </Label>
            <Input
              id="su-max-licenses"
              type="number"
              min={0}
              value={maxLicenses}
              onChange={(e) => setMaxLicenses(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-discount-type">
              {t("partners.superUsers.discountTypeLabel")}
            </Label>
            <select
              id="su-discount-type"
              value={discountType ?? "percentage"}
              onChange={(e) =>
                setDiscountType(e.target.value as PatchedSuperUser["discount_type"])
              }
              className="flex min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="percentage">
                {t("partners.superUsers.discountPercentage")}
              </option>
              <option value="fixed">{t("partners.superUsers.discountFixed")}</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-discount-value">
              {t("partners.superUsers.discountValueLabel")}
            </Label>
            <Input
              id="su-discount-value"
              type="number"
              min={0}
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-discount-duration">
              {t("partners.superUsers.discountDurationLabel")}
            </Label>
            <Input
              id="su-discount-duration"
              type="number"
              min={0}
              value={discountDuration}
              onChange={(e) => setDiscountDuration(e.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
            <Label htmlFor="su-pre-groups">
              {t("partners.inviteSuperUser.preGroupsLabel")}
            </Label>
            <textarea
              id="su-pre-groups"
              rows={2}
              value={preGroups}
              placeholder={t("partners.inviteSuperUser.preGroupsPlaceholder")}
              onChange={(e) => setPreGroups(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <Button type="submit" disabled={invite.isPending || email.trim() === ""}>
              {invite.isPending
                ? t("partners.inviteSuperUser.sending")
                : t("partners.inviteSuperUser.submit")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** Inline invite form for a new local partner (admin provisioning). */
function InviteLocalPartnerForm() {
  const { t } = useTranslation();
  const invite = useInviteLocalPartner();
  const [email, setEmail] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    invite.mutate(
      { email: email.trim() },
      {
        onSuccess: () => {
          toast.success(t("partners.inviteLocalPartner.sent", { email: email.trim() }));
          setEmail("");
        },
        onError: (err) => toast.error(apiErrorDetail(err) ?? t("partners.actionFailed")),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {t("partners.inviteLocalPartner.heading")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1 space-y-1.5">
            <Label htmlFor="lp-email">
              {t("partners.inviteLocalPartner.emailLabel")}
            </Label>
            <Input
              id="lp-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={invite.isPending || email.trim() === ""}>
            {invite.isPending
              ? t("partners.inviteLocalPartner.sending")
              : t("partners.inviteLocalPartner.submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** Pending regional + local-partner invitations, with resend / cancel. */
function PendingInvitationsPanel() {
  const { t } = useTranslation();
  const invitations = usePendingInvitations();
  const resend = useResendInvitation();
  const cancel = useCancelInvitation();

  const typeLabel = (type: string) =>
    type === InvitationType.SuperUser
      ? t("partners.invitations.typeRegional")
      : t("partners.invitations.typeLocal");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t("partners.invitations.heading")}</CardTitle>
      </CardHeader>
      <CardContent>
        {invitations.isPending ? (
          <p className="text-sm text-muted-foreground">{t("partners.loading")}</p>
        ) : invitations.isError ? (
          <p className="text-sm text-destructive">{t("partners.loadError")}</p>
        ) : (invitations.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("partners.invitations.empty")}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {invitations.data!.map((inv) => (
              <li
                key={inv.id}
                className="flex flex-wrap items-center justify-between gap-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{inv.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {typeLabel(inv.invitation_type ?? "")}
                    {inv.group_name ? ` · ${inv.group_name}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resend.mutate(inv.id)}
                    disabled={resend.isPending}
                  >
                    {t("partners.invitations.resend")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => cancel.mutate(inv.id)}
                    disabled={cancel.isPending}
                  >
                    {t("partners.invitations.cancel")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface LpPoolValues {
  max_group_licenses: string;
  discount_type: PatchedLocalPartner["discount_type"];
  discount_value: string;
  discount_duration_years: string;
}

/** Autosaving pool/discount editor for one local partner (mirrors PoolEditor). */
function LocalPartnerPoolEditor({
  lp,
  onClose,
}: {
  lp: LocalPartner;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isPercentage = lp.discount_type !== "fixed";
  const { form, autosave } = useAutosaveForm<LpPoolValues>({
    defaultValues: {
      max_group_licenses: toStr(lp.max_group_licenses),
      discount_type: lp.discount_type ?? "percentage",
      discount_value: isPercentage
        ? (lp.discount_percentage ?? "")
        : (lp.discount_amount ?? ""),
      discount_duration_years: toStr(lp.discount_duration_years),
    },
    autosave: {
      save: (values, signal) => {
        const percentage = values.discount_type === "percentage";
        const body: PatchedLocalPartner = {
          max_group_licenses:
            values.max_group_licenses.trim() === ""
              ? null
              : Number(values.max_group_licenses),
          discount_type: values.discount_type,
          discount_percentage:
            percentage && values.discount_value.trim() !== ""
              ? values.discount_value
              : null,
          discount_amount:
            !percentage && values.discount_value.trim() !== ""
              ? values.discount_value
              : null,
          discount_duration_years:
            values.discount_duration_years.trim() === ""
              ? null
              : Number(values.discount_duration_years),
        };
        return saveLocalPartner(lp.id, body, signal);
      },
      debounceMs: 600,
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between">
        <CardTitle className="text-base">
          {t("partners.localPartners.editHeading", { name: fullName(lp) })}
        </CardTitle>
        <SaveStatus status={autosave.status} />
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={(e) => e.preventDefault()}>
          <form.Field name="max_group_licenses">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.superUsers.maxLicensesLabel")}
              />
            )}
          </form.Field>
          <form.Field name="discount_type">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor={field.name}>
                  {t("partners.superUsers.discountTypeLabel")}
                </Label>
                <select
                  id={field.name}
                  name={field.name}
                  value={field.state.value ?? "percentage"}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value as PatchedLocalPartner["discount_type"],
                    )
                  }
                  onBlur={field.handleBlur}
                  className="flex min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="percentage">
                    {t("partners.superUsers.discountPercentage")}
                  </option>
                  <option value="fixed">
                    {t("partners.superUsers.discountFixed")}
                  </option>
                </select>
              </div>
            )}
          </form.Field>
          <form.Field name="discount_value">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.superUsers.discountValueLabel")}
              />
            )}
          </form.Field>
          <form.Field name="discount_duration_years">
            {(field) => (
              <TextField
                field={field}
                type="number"
                label={t("partners.superUsers.discountDurationLabel")}
              />
            )}
          </form.Field>
        </form>
        <div className="mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t("partners.superUsers.close")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Admin-only list of local partners with per-LP pool/discount editing. */
function LocalPartnerSection() {
  const { t } = useTranslation();
  const localPartners = useLocalPartnersQuery();
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-semibold">{t("partners.localPartners.heading")}</h2>

      {localPartners.isLoading && (
        <p className="text-sm text-muted-foreground">{t("partners.loading")}</p>
      )}
      {localPartners.isError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <p className="text-sm text-destructive">{t("partners.loadError")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void localPartners.refetch()}
            >
              {t("partners.retry")}
            </Button>
          </CardContent>
        </Card>
      )}
      {localPartners.data && localPartners.data.length === 0 && (
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-muted-foreground">
              {t("partners.localPartners.empty")}
            </p>
          </CardContent>
        </Card>
      )}

      {localPartners.data && localPartners.data.length > 0 && (
        <>
          {/* Mobile: one card per local partner; the table squishes on a phone. */}
          <ul className="space-y-2 md:hidden">
            {localPartners.data.map((lp) => (
              <li key={lp.id} className="rounded-lg border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{fullName(lp)}</div>
                    <div className="break-words text-xs text-muted-foreground">
                      {lp.email}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId((cur) => (cur === lp.id ? null : lp.id))}
                  >
                    {t("partners.superUsers.edit")}
                  </Button>
                </div>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">
                    {t("partners.localPartners.colGroups")}
                  </dt>
                  <dd>{lp.group_names.join(", ") || "—"}</dd>
                  <dt className="text-muted-foreground">
                    {t("partners.superUsers.colMaxLicenses")}
                  </dt>
                  <dd>{licencePool(lp, t)}</dd>
                </dl>
              </li>
            ))}
          </ul>

          <Card className="hidden md:block">
            <CardContent className="overflow-x-auto py-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.superUsers.colName")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.superUsers.colEmail")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.localPartners.colGroups")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.superUsers.colMaxLicenses")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      <span className="sr-only">{t("partners.superUsers.edit")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {localPartners.data.map((lp) => (
                    <tr key={lp.id} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{fullName(lp)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{lp.email}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {lp.group_names.join(", ") || "—"}
                      </td>
                      <td className="px-3 py-2">{licencePool(lp, t)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setEditingId((cur) => (cur === lp.id ? null : lp.id))
                          }
                        >
                          {t("partners.superUsers.edit")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {editingId && localPartners.data?.find((lp) => lp.id === editingId) && (
        <LocalPartnerPoolEditor
          key={editingId}
          lp={localPartners.data.find((lp) => lp.id === editingId)!}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}

/** Admin-only management of regional partners (User.Role.SuperUser). */
export function SuperUserManagement() {
  const { t } = useTranslation();
  const superUsers = useSuperUsersQuery();
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">{t("partners.superUsers.heading")}</h2>
        <p className="mt-1 text-muted-foreground">{t("partners.superUsers.subtitle")}</p>
      </div>

      <InviteSuperUserForm />
      <InviteLocalPartnerForm />
      <PendingInvitationsPanel />

      {superUsers.isLoading && (
        <p className="text-sm text-muted-foreground">{t("partners.loading")}</p>
      )}

      {superUsers.isError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <p className="text-sm text-destructive">{t("partners.loadError")}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void superUsers.refetch()}
            >
              {t("partners.retry")}
            </Button>
          </CardContent>
        </Card>
      )}

      {superUsers.data && superUsers.data.length === 0 && (
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-muted-foreground">
              {t("partners.superUsers.empty")}
            </p>
          </CardContent>
        </Card>
      )}

      {superUsers.data && superUsers.data.length > 0 && (
        <>
          {/* Mobile: one card per super user; the table squishes on a phone. */}
          <ul className="space-y-2 md:hidden">
            {superUsers.data.map((u) => (
              <li key={u.id} className="rounded-lg border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{fullName(u)}</div>
                    <div className="break-words text-xs text-muted-foreground">
                      {u.email}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingId((cur) => (cur === u.id ? null : u.id))}
                  >
                    {t("partners.superUsers.edit")}
                  </Button>
                </div>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">
                    {t("partners.superUsers.colMaxGroups")}
                  </dt>
                  <dd>{u.max_groups ?? t("partners.superUsers.unlimited")}</dd>
                  <dt className="text-muted-foreground">
                    {t("partners.superUsers.colMaxLicenses")}
                  </dt>
                  <dd>{u.max_group_licenses ?? t("partners.superUsers.unlimited")}</dd>
                </dl>
              </li>
            ))}
          </ul>

          <Card className="hidden md:block">
            <CardContent className="overflow-x-auto py-4">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.superUsers.colName")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.superUsers.colEmail")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.superUsers.colMaxGroups")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      {t("partners.superUsers.colMaxLicenses")}
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      <span className="sr-only">{t("partners.superUsers.edit")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {superUsers.data.map((u) => (
                    <tr key={u.id} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{fullName(u)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{u.email}</td>
                      <td className="px-3 py-2">
                        {u.max_groups ?? t("partners.superUsers.unlimited")}
                      </td>
                      <td className="px-3 py-2">
                        {u.max_group_licenses ?? t("partners.superUsers.unlimited")}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setEditingId((cur) => (cur === u.id ? null : u.id))
                          }
                        >
                          {t("partners.superUsers.edit")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {editingId &&
        superUsers.data?.find((u) => u.id === editingId) && (
          <PoolEditor
            key={editingId}
            user={superUsers.data.find((u) => u.id === editingId)!}
            onClose={() => setEditingId(null)}
          />
        )}

      <div className="border-t border-border pt-6">
        <LocalPartnerSection />
      </div>
    </div>
  );
}
